import { Alert, Typography } from "antd";
import AIButton from "components/molecules/AIButton";
import { useAppSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";

interface SchemaAskAIContentProps {
    manageSchemaModelRef: React.RefObject<any>;
}

export default function SchemaAskAIContent({ manageSchemaModelRef }: SchemaAskAIContentProps) {
    const schemaModelsState = useAppSelector((state) => state.schemaModels as any);
    const generateSchemaHealthReportResult = schemaModelsState.generateSchemaHealthReportResult;
    const generateDdlSqlResult = schemaModelsState.generateDdlSqlResult;
    const generateGapPredictionResult = schemaModelsState.generateGapPredictionResult;

    const dataTagsState = useAppSelector((state) => state.dataTags as any);
    const autoTaggingResult = dataTagsState.autoTaggingResult;

    // Force re-render when ref.current changes
    const [, setRefValue] = useState(null);

    useEffect(() => {
        if (manageSchemaModelRef?.current) {
            setRefValue(manageSchemaModelRef.current);
        }

        // Check periodically for ref changes (only while null)
        const interval = setInterval(() => {
            if (manageSchemaModelRef?.current) {
                setRefValue(manageSchemaModelRef.current);
                clearInterval(interval);
            }
        }, 300);

        return () => clearInterval(interval);
    }, [manageSchemaModelRef]);

    const controller = manageSchemaModelRef?.current;
    const hasController = !!controller;
    const isReadOnly = controller?.isReadOnlyView ? controller.isReadOnlyView() : true;
    const hasOpenAiKey = !!controller?.props?.keyExists?.exists;
    const action = controller?.props?.action;
    const schemaId = controller?.props?.schemaModel?.schemaId || controller?.props?.defaultValue?.schemaId;
    const hasSavedSchema = !!schemaId;
    const showSavedSchemaTools = action !== "create" && hasSavedSchema;

    const openTool = (methodName: string) => () => {
        const method = controller?.[methodName];
        if (typeof method === "function") {
            method.call(controller);
        }
    };

    const isInternalAutoTaggingLoading = !!controller?.state?.isAutoTagging || !!controller?.state?.isFetchingTags || !!controller?.state?.isAutoTagRequested;
    const isAutoTagLoading = isInternalAutoTaggingLoading || !!autoTaggingResult?.loading;

    const schemaToolDisabled = !hasController || isReadOnly || !hasOpenAiKey;
    const schemaToolDisabledTitle = !hasController
        ? "Loading schema editor..."
        : !hasOpenAiKey
          ? "OpenAI API key is not configured. Please add it in System Keys to enable this feature."
          : "Disabled in view mode";

    const viewModeToolDisabled = !hasController || !hasOpenAiKey;
    const viewModeToolDisabledTitle = !hasController
        ? "Loading schema editor..."
        : !hasOpenAiKey
          ? "OpenAI API key is not configured. Please add it in System Keys to enable this feature."
          : "";

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 bg-white p-4">
            <div className="space-y-2">
                <Typography.Title level={5} className="!mb-0">
                    Schema AI Tools
                </Typography.Title>
                <Typography.Paragraph className="!mb-0 text-sm text-gray-600">Run schema-specific AI actions from here.</Typography.Paragraph>
            </div>

            {!hasController && <Alert type="info" showIcon message="Loading schema editor context..." />}

            <div className="grid gap-3">
                <AIButton
                    onClick={openTool("triggerAutoTagging")}
                    disabled={schemaToolDisabled}
                    loading={isAutoTagLoading}
                    tooltipTitle="Generate classifier suggestions for the current schema"
                    disabledTooltipTitle={schemaToolDisabledTitle}
                    buttonText="Auto-Tag"
                />
                {showSavedSchemaTools && (
                    <>
                        <AIButton
                            onClick={openTool("showPIISuggestionsModal")}
                            disabled={schemaToolDisabled}
                            loading={false}
                            tooltipTitle="Analyze schema fields for PII classification with AI"
                            disabledTooltipTitle={schemaToolDisabledTitle}
                            buttonText="Auto-PII Tagging"
                        />
                        <AIButton
                            onClick={openTool("showFieldDescriptionsModal")}
                            disabled={schemaToolDisabled}
                            loading={false}
                            tooltipTitle="Generate business-friendly field descriptions with AI"
                            disabledTooltipTitle={schemaToolDisabledTitle}
                            buttonText="Auto-Describe"
                        />
                        <AIButton
                            onClick={openTool("showDdlSqlModal")}
                            disabled={viewModeToolDisabled}
                            loading={!!generateDdlSqlResult?.loading}
                            tooltipTitle="Generate DDL SQL export for this schema"
                            disabledTooltipTitle={viewModeToolDisabledTitle}
                            buttonText="DDL"
                        />
                        <AIButton
                            onClick={openTool("showGapPredictionModal")}
                            disabled={viewModeToolDisabled}
                            loading={!!generateGapPredictionResult?.loading}
                            tooltipTitle="Predict architectural gaps in this schema with AI"
                            disabledTooltipTitle={viewModeToolDisabledTitle}
                            buttonText="Predict Gaps"
                        />
                        <AIButton
                            onClick={openTool("showHealthReportModal")}
                            disabled={viewModeToolDisabled}
                            loading={!!generateSchemaHealthReportResult?.loading}
                            tooltipTitle="Generate a governance health report for this schema"
                            disabledTooltipTitle={viewModeToolDisabledTitle}
                            buttonText="Health Report"
                        />
                    </>
                )}
            </div>
        </div>
    );
}
